import React, { useState, useEffect, useCallback } from "react";
import { Check, ChevronsUpDown, BookOpen, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { supabase } from "@/integrations/supabase/client";

interface BookOption {
  id: number;
  title: string;
  authors: string;
  coverImage?: string;
}

interface BookPickerProps {
  /** Currently selected book (null if none) */
  selectedBook: BookOption | null;
  /** Called when a book is selected or cleared */
  onSelect: (book: BookOption | null) => void;
  /** Whether the field is required */
  required?: boolean;
}

export default function BookPicker({
  selectedBook,
  onSelect,
  required = false,
}: BookPickerProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [books, setBooks] = useState<BookOption[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchBooks = useCallback(async (query: string) => {
    try {
      setLoading(true);

      // Fetch books matching the search
      let bookQuery = supabase
        .from("books")
        .select("id, title, cover_image")
        .eq("is_active", true)
        .order("title")
        .limit(30);

      if (query.trim()) {
        bookQuery = bookQuery.ilike("title", `%${query.trim()}%`);
      }

      const { data: booksData, error: booksError } = await bookQuery;
      if (booksError) throw booksError;
      if (!booksData || booksData.length === 0) {
        setBooks([]);
        return;
      }

      const bookIds = booksData.map((b) => b.id);

      // Fetch authors for these books
      const { data: authorsData, error: authorsError } = await supabase
        .from("books_authors")
        .select(
          `
          book_id,
          people!books_authors_person_id_fkey(
            name
          )
        `
        )
        .in("book_id", bookIds);

      if (authorsError) throw authorsError;

      // Build author lookup
      const authorsByBookId: Record<number, string[]> = {};
      if (authorsData) {
        authorsData.forEach((item) => {
          if (!authorsByBookId[item.book_id]) {
            authorsByBookId[item.book_id] = [];
          }
          if (item.people && typeof item.people === "object" && "name" in item.people) {
            authorsByBookId[item.book_id].push(
              (item.people as { name: string }).name
            );
          }
        });
      }

      // Also search by author name if query provided
      let authorMatchBookIds: number[] = [];
      if (query.trim()) {
        const { data: authorSearch } = await supabase
          .from("people")
          .select("id")
          .ilike("name", `%${query.trim()}%`);

        if (authorSearch && authorSearch.length > 0) {
          const { data: bookAuthorLinks } = await supabase
            .from("books_authors")
            .select("book_id")
            .in(
              "person_id",
              authorSearch.map((a) => a.id)
            );

          if (bookAuthorLinks) {
            authorMatchBookIds = bookAuthorLinks.map((l) => l.book_id);
          }
        }

        // Fetch additional books matched by author (not already in results)
        const missingIds = authorMatchBookIds.filter(
          (id) => !bookIds.includes(id)
        );
        if (missingIds.length > 0) {
          const { data: extraBooks } = await supabase
            .from("books")
            .select("id, title, cover_image")
            .eq("is_active", true)
            .in("id", missingIds)
            .limit(10);

          if (extraBooks) {
            // Fetch authors for extra books too
            const extraIds = extraBooks.map((b) => b.id);
            const { data: extraAuthors } = await supabase
              .from("books_authors")
              .select(
                `
                book_id,
                people!books_authors_person_id_fkey(name)
              `
              )
              .in("book_id", extraIds);

            if (extraAuthors) {
              extraAuthors.forEach((item) => {
                if (!authorsByBookId[item.book_id]) {
                  authorsByBookId[item.book_id] = [];
                }
                if (item.people && typeof item.people === "object" && "name" in item.people) {
                  authorsByBookId[item.book_id].push(
                    (item.people as { name: string }).name
                  );
                }
              });
            }

            booksData.push(...extraBooks);
          }
        }
      }

      const options: BookOption[] = booksData.map((book) => ({
        id: book.id,
        title: book.title,
        authors: (authorsByBookId[book.id] || []).join(", ") || "Unknown",
        coverImage: book.cover_image || undefined,
      }));

      setBooks(options);
    } catch (error) {
      console.error("Error fetching books for picker:", error);
      setBooks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + debounced search
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchBooks(searchQuery);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery, fetchBooks]);

  // Load initial books when popover opens
  useEffect(() => {
    if (open && books.length === 0) {
      fetchBooks("");
    }
  }, [open, books.length, fetchBooks]);

  return (
    <div>
      <Label className="text-xs text-muted-foreground">
        <BookOpen className="inline h-3 w-3 mr-1" />
        Select Book
        {required && <span className="text-destructive ml-0.5">*</span>}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between h-auto min-h-[2rem] py-1.5 text-sm font-normal",
              !selectedBook && "text-muted-foreground"
            )}
          >
            {selectedBook ? (
              <div className="flex items-center gap-2 text-left min-w-0">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {selectedBook.title}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    by {selectedBook.authors}
                  </div>
                </div>
              </div>
            ) : (
              "Search and select a book..."
            )}
            <div className="flex items-center gap-1 shrink-0 ml-2">
              {selectedBook && (
                <span
                  role="button"
                  className="h-4 w-4 rounded-sm hover:bg-muted flex items-center justify-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(null);
                  }}
                >
                  <X className="h-3 w-3" />
                </span>
              )}
              <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search by title or author..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {loading ? (
                <div className="py-4 text-center text-sm text-muted-foreground">
                  Searching...
                </div>
              ) : (
                <>
                  <CommandEmpty>No books found.</CommandEmpty>
                  <CommandGroup>
                    {books.map((book) => (
                      <CommandItem
                        key={book.id}
                        value={book.id.toString()}
                        onSelect={() => {
                          onSelect(book);
                          setOpen(false);
                          setSearchQuery("");
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4 shrink-0",
                            selectedBook?.id === book.id
                              ? "opacity-100"
                              : "opacity-0"
                          )}
                        />
                        <div className="min-w-0">
                          <div className="text-sm truncate">{book.title}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            by {book.authors}
                          </div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
